import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { services } from '../mock';
import { CheckCircle, ArrowRight, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

// Service-specific content
const serviceContent = {
  'residential-school': {
    subtitle: (
      <>
        <b>Fresh, Clean Uniforms for Happy School Days.</b>
        <br />
        <br />
        We offer a reliable school laundry service designed to support cleanliness, comfort, and discipline. From student uniforms to school bedding, we ensure every item is washed hygienically and handled with care so students look neat and feel confident every day
      </>
    ),
    processTitle: 'How We Handle  Residential School Laundry',
    processDescription:
      'Our school laundry service is built around hygiene, discipline, and consistency. We handle uniforms, bedding, and school textiles with care, ensuring professional washing, drying, and finishing. This reduces hostel staff workload and ensures students always have clean, ready-to-use clothes.',
    whoFor: [
      'Residential Boarding Schools',
      'CBSE / ICSE / State Board Residential Schools',
      'International Schools with Boarding Facilities',
      'Gurukuls & Residential Academies',
      'K–12 Residential Campuses',
    ],
    challenges: [
      { problem: 'Students depend fully on staff', solution: 'Laundry managed externally' },
      { problem: 'Parents worry about cleanliness', solution: 'Hygiene-focused washing methods' },
      { problem: 'Uniforms wear out quickly', solution: 'Gentle, fabric-safe care' },
      { problem: 'Laundry schedules get missed', solution: 'Fixed service routines' },
      { problem: 'Mixing of student clothes', solution: 'Clear tagging & identification' },
      { problem: 'High staff workload', solution: 'Reduced internal handling' }
    ],
    benefits: [
      'Designed with children’s comfort in mind',
      'Suitable for daily uniform use',
      'Consistent service throughout the year',
      'Reduces laundry-related parent concerns',
      'Cleaner hostel environments',
      'Easy coordination with school authorities',
      'Reliable during peak school schedules',
      'Maintains discipline through routine'
    ]
  },
  'pu-laundry': {
    subtitle: (
      <>
        <b>Hygienic, Reliable Laundry Solutions for Residential Academic Excellence
        </b>
        <br />
        <br />
        We provide clean, hygienic, and reliable laundry services for Residential PU Colleges and Integrated Coaching Campuses. Designed for institutions with NEET, JEE, CET, and foundation coaching, our service ensures students receive fresh clothes on time without disrupting their study schedules.
      </>
    ),
    processTitle: 'How We Handle PU Laundry',
    processDescription: (
      <>
        Our structured laundry process is designed for fully residential PU and junior college campuses. With scheduled pickup and delivery, students receive clean clothes without queues or delays, helping management maintain hygiene standards while students stay focused on academics and exam preparation.
      </>
    ),
    whoFor: ['PU Colleges with Integrated NEET Coaching', 'PU Colleges with Integrated JEE / CET Coaching', 'Residential Junior Colleges', 'Pre-University Boarding Campuses', 'Integrated Foundation Academies'],
    challenges: [
      { problem: 'No time for laundry', solution: 'Fully managed service' },
      { problem: 'Lack of washing space', solution: 'Professional infrastructure' },
      { problem: 'Clothes get mixed', solution: 'Proper tagging & tracking' },
      { problem: 'Laundry stress', solution: 'Regular pickup schedules' },
      { problem: 'Hygiene concerns', solution: 'Sanitized wash process' },
      { problem: 'Staff burden', solution: 'End-to-end management' }
    ],
    benefits: [
      'Tailored for Residential PU Campuses',
      'High Hygiene & Safety Standards',
      'Supports Competitive Exam Preparation',
      'Uniform & Hostel Laundry Management',
      'Reliable Weekly Pickup & Delivery',
      'Affordable Institutional Pricing',
      'Improved Hostel Organization',
      'Peace of Mind for Parents & Management'
    ]
  },

  'industrial-laundry': {
    subtitle: (
      <>
        <b>Professional Laundry Solutions for High-Volume Commercial Operations</b>
        <br />
        <br />
        We provide reliable, hygienic, and scalable laundry services for hospitality and industrial institutions, supporting operations where cleanliness, consistency, and turnaround time are critical. From hotels and resorts to manufacturing plants and large corporate campuses, our laundry solutions are designed to handle high-volume, continuous-use environments with efficiency and care.
      </>
    ),
    processTitle: 'How We Handle Hospitality & Industrial Institutions',
    processDescription:
      'Hospitality and industrial facilities require a fast, organized, and dependable laundry system to support daily operations without interruption. Our scheduled pickup and delivery model ensures smooth laundry flow across departments and facilities',
    whoFor: [
      'Hotel linens, bedding, and towels',
      'Staff uniforms and workwear',
      'Industrial and corporate laundry requirements',
      'Bulk laundry for large operational teams',
    ],
    challenges: [
      { problem: 'Large laundry volumes', solution: 'High-capacity processing' },
      { problem: 'Heavy stains', solution: 'Pre-treatment & inspection' },
      { problem: 'Fabric damage', solution: 'Fabric-specific wash cycles' },
      { problem: 'Operational delays', solution: 'Scheduled pickup & delivery' },
      { problem: 'Strict hygiene rules', solution: 'Eco-friendly sanitization' },
      { problem: 'Tracking issues', solution: 'Cloth coding & batch tracking' },
      { problem: 'Extra manpower required', solution: 'Fully outsourced service' }
    ],
    benefits: [
      'Built for High-Volume Operations',
      'Commercial-Grade Hygiene Standards',
      'Fast & Reliable Turnaround Times',
      'Uniform & Linen Management',
      'Scalable Service Models',
      'Cost-Effective Commercial Pricing',
      'Reduced Operational Burden',
      'Consistent Quality & Professional Handling'
    ]
  },
  'universities-laundry': {
    subtitle: (
      <>
        <b>Professional Laundry Services for Higher Education & Healthcare Campuses
        </b>
        <br />
        <br />
        We provide reliable, hygienic, and large-scale laundry services for colleges, universities, and medical & healthcare institutions with hostels, residential blocks, hospitals, clinics, and staff housing. Our laundry solutions are designed to support academic campuses, medical colleges, and healthcare facilities where cleanliness, consistency, and timely service are essential for daily operations.
      </>
    ),
    processTitle: 'How We Handle College Laundry',
    processDescription: (
      <>
        Education and healthcare institutions require a structured, high-capacity laundry system that works without disrupting schedules or compromising hygiene. Our scheduled pickup and delivery model ensures smooth laundry operations across campuses, hostels, hospitals, and residential facilities.
      </>
    ),
    whoFor: ['Engineering Colleges', 'Medical & Dental Colleges', 'Nursing & Paramedical Colleges', 'Degree Colleges', 'Universities & Deemed Universities', 'Autonomous Colleges', 'Institutional Hostels & Student Housing', 'Multi-Speciality Hospitals', 'Super-Speciality Hospitals', 'Clinics & Diagnostic Centres'],
    challenges: [
      { problem: 'No time for laundry', solution: 'Fully managed service' },
      { problem: 'Lack of washing space', solution: 'Professional infrastructure' },
      { problem: 'Clothes get mixed', solution: 'Proper tagging & tracking' },
      { problem: 'Laundry stress', solution: 'Regular pickup schedules' },
      { problem: 'Hygiene concerns', solution: 'Sanitized wash process' },
      { problem: 'Staff burden', solution: 'End-to-end management' }
    ],
    benefits: [
      'Built for Large Institutional Campuse',
      'High Hygiene & Safety Standards',
      'Safe Handling of Medical & Institutional Textiles',
      'Reliable Pickup & Delivery Schedules',
      'Scalable & Centralized Laundry Management',
      'Cost-Effective Institutional Pricing',
      'Reduced Operational & Administrative Load',
      'Peace of Mind for Management, Staff & Parents'
    ]
  },
};

const ServiceDetail = () => {
  const { slug } = useParams();
  const service = services.find(s => s.slug === slug);
  const content = serviceContent[slug];

  if (!service || !content) {
    return (
      <div className="min-h-screen flex items-center justify-center text-center">
        <div>
          <h1 className="text-4xl font-bold mb-4">Service Not Found</h1>
          <Link to="/">
            <Button className="bg-sky-600 hover:bg-sky-700">Go Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  const {
    subtitle,
    processTitle,
    processDescription,
    whoFor = [],
    challenges = [],
    benefits = []
  } = content;

  const whoWeServeLayout =
    whoFor.length >= 5
      ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-6xl'
      : whoFor.length === 4
        ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 max-w-7xl'
        : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-4xl';

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-sky-50">
      {/* Hero — extended pb so blue band is taller, no abrupt cutoff */}
      <section className="bg-gradient-to-br from-blue-900 to-sky-700 text-white pt-8 pb-16 md:pt-10 md:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col lg:grid lg:grid-cols-2 gap-6 md:gap-8 items-center">
          <div>
            <Link to="/" className="inline-flex items-center mb-4 md:mb-6 text-sky-100 hover:text-white text-sm">
              <ArrowLeft className="mr-2 h-4 w-4" /> Back to Home
            </Link>
            <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold mb-4 md:mb-6">{service.title}</h1>
            <p className="text-sm md:text-base mb-6 md:mb-8 text-sky-100 leading-relaxed">{subtitle}</p>
            <Link to="/contact">
              <Button size="lg" className="bg-white text-sky-600 hover:bg-sky-50 w-full sm:w-auto">
                Get in Touch <ArrowRight className="ml-2" />
              </Button>
            </Link>
          </div>
          <img
            src={service.image}
            alt={service.title}
            className="w-full h-44 sm:h-64 md:h-80 lg:h-96 object-cover rounded-2xl mt-4 lg:mt-0"
          />
        </div>
      </section>

      {/* Process Description — rounded white card overlaps hero bottom */}
      <section className="relative -mt-10 z-10 bg-white rounded-t-3xl px-4 sm:px-6 lg:px-8 pt-10 pb-6 text-center">
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-blue-900 mb-4">{processTitle}</h2>
        <p className="max-w-4xl mx-auto text-gray-700 text-base leading-relaxed text-justify">{processDescription}</p>
      </section>

      <section className="py-10 md:py-16 bg-white px-4 sm:px-6 lg:px-8">
  <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-center text-blue-900 mb-8 md:mb-12">
    Who We Serve
  </h2>

  <div className="flex justify-center">
    <div className="flex flex-wrap gap-3 md:gap-6 justify-center w-full max-w-7xl">
      {whoFor.map((item, i) => {
        let colWidth;
        if (whoFor.length >= 5) {
          colWidth = 'sm:w-1/2 md:w-1/3 lg:w-1/4';
        } else if (whoFor.length === 4) {
          colWidth = 'sm:w-1/2 lg:w-1/4';
        } else {
          colWidth = 'sm:w-1/2 lg:w-1/3';
        }
        return (
          <div key={i} className={`w-full ${colWidth}`}>
            <Card
              className="bg-blue-100 border-sky-100 h-40 flex items-center justify-center hover:shadow-md transition-shadow"
            >
              <CardContent className="p-4 md:p-6 text-center w-full">
                <CheckCircle className="mx-auto mb-3 text-sky-600 h-6 w-6" />
                <p className="font-medium text-gray-700 text-sm md:text-base leading-snug">{item}</p>
              </CardContent>
            </Card>
          </div>
        );
      })}
    </div>
  </div>
</section>

      {/* Benefits */}
      <section className="py-10 md:py-16 bg-sky-50 px-4 sm:px-6 lg:px-8">
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-center text-blue-900 mb-10 md:mb-12">
          What Makes Our Service Special
        </h2>
        <div className="flex justify-center">
          <div className="w-full max-w-7xl">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {benefits.map((b, i) => {
                return (
                  <div key={i} className="flex gap-4 bg-white border border-sky-100 p-5 md:p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
                    <CheckCircle className="text-sky-600 mt-1 flex-shrink-0 h-5 w-5" />
                    <span className="text-gray-700 text-sm md:text-base leading-relaxed">{b}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;